import { Box, Stack, Typography } from '@mui/material'
import { ReactNode } from 'react'
import { Color, GameTreePosition } from '../chess/types'
import { GameViewState } from '../features/gameView/GameViewModel'
import { MaterialBalance } from '../features/gameView/components/materialBalance/MaterialBalance'
import { PlayerInfo } from '../functions/getPlayerInfos'
import { IOverallRecord } from '../functions/getPlayersStats'
import { imgFallback } from '../helpers/imgFallback'
import { FramedAvatar } from '../sharedComponents/src/globalHeader/components/FramedAvatar'
import { RatingsObject } from '../store/types'
import ProfileInfo from './ProfileInfo/ProfileInfo'
import Rating from './Rating/Rating'
import { UserTitle } from './userTitle/UserTitle'

const avatarStyle = {
    height: '48px',
    width: '48px',
}

export enum PageView {
    GAME = 'game',
    OBSERVE = 'observe',
    ANALYSIS = 'analysis',
}

interface PlayerInfoBarProps {
    player: PlayerInfo | undefined
    gameState: GameViewState | undefined
    gtpos: GameTreePosition | undefined
    clock: JSX.Element | undefined
    color: Color
    view: PageView
    latency?: number
    top: boolean
    overallRecord?: IOverallRecord
    totalScore?: number
    totalScoreColor?: string
    showRating?: boolean
    emote?: ReactNode // PvP emote bubble, anchored to the avatar (game view only)
}

export const PlayerInfoBar = (props: PlayerInfoBarProps) => {
    const { player, gameState, gtpos, clock, color, top, overallRecord, view, showRating, emote } = props

    const defaultRatings: RatingsObject = {
        blitz: {
            rating: 0,
            win: 0,
            draw: 0,
            loss: 0,
            game_type: 'blitz',
            total_games: 0,
            is_provisional: false,
        },
        bullet: {
            rating: 0,
            win: 0,
            draw: 0,
            loss: 0,
            game_type: 'bullet',
            total_games: 0,
            is_provisional: false,
        },
        rapid: {
            rating: 0,
            win: 0,
            draw: 0,
            loss: 0,
            game_type: 'rapid',
            total_games: 0,
            is_provisional: false,
        },
        classic: {
            rating: 0,
            win: 0,
            draw: 0,
            loss: 0,
            game_type: 'classic',
            total_games: 0,
            is_provisional: false,
        },
    }

    // ------------- Subcomponents -------------

    const materialBalance = gameState !== GameViewState.PRE_GAME && gtpos && (
        <MaterialBalance pieces={gtpos.position.board} color={color} size={'22px'} />
    )

    const progress = (
        <Box flexGrow={1} flexDirection={top ? 'column' : 'column-reverse'} display="flex" justifyContent="flex-end">
            {clock}
            <Box overflow="visible">{materialBalance}</Box>
        </Box>
    )

    const avatar =
        player &&
        (view === PageView.ANALYSIS && !player.avatarUrl ? (
            <div
                style={{
                    height: '56px',
                    width: '56px',
                    border: '2px solid white',
                    borderRadius: '12px',
                    backgroundColor: color === Color.White ? 'white' : 'black',
                }}
            />
        ) : (
            <ProfileInfo
                user={{
                    avatarUrl: player.avatarUrl || '',
                    country: player.nationality || '',
                    ratings: player.ratings || defaultRatings,
                    title: player.title || '',
                    id: player.playerId || '',
                    userName: player.name || '',
                    status: 'online',
                    reward: {
                        tier: player.reward?.tier || 0,
                        season: player.reward?.season || '',
                    },
                }}
                origin={top ? 'bottom' : 'top'}
                showOnClick={view === PageView.OBSERVE ? true : false}
                showOnHover={player.human && view !== PageView.OBSERVE}
                overallRecord={overallRecord}
            >
                <FramedAvatar
                    avatar={player.avatarUrl || ''}
                    season={player.reward?.season}
                    reward={player.reward?.tier}
                    sx={avatarStyle}
                />
            </ProfileInfo>
        ))

    const playerInfo = player ? (
        <Stack direction="row" gap={1}>
            <Stack gap={0.75} minWidth={'50px'} justifyContent="space-between">
                <Stack direction="row" alignItems="center" gap={1}>
                    <Typography
                        sx={{
                            flexGrow: 1,
                        }}
                        variant="body2"
                        noWrap
                        textAlign="end"
                    >
                        {player ? player.name : ''}
                    </Typography>
                </Stack>

                <Box
                    sx={{
                        height: '24px',
                        gap: 0.5,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        flexWrap: 'nowrap',
                    }}
                >
                    {player.botdRewardUrl && (
                        <img
                            src={player.botdRewardUrl}
                            alt={'Bot of the day reward'}
                            style={{
                                height: '22px',
                            }}
                        />
                    )}
                    {player && player.title && <UserTitle title={player.title} />}
                    {showRating &&
                        player &&
                        player.human !== false &&
                        player.rating !== undefined &&
                        !Number.isNaN(player.rating) &&
                        Boolean(player.rating) &&
                        player.ratings &&
                        player.ratingType && (
                            <Rating data={player.ratings[player.ratingType]} textSize="body1" iconSize="medium" />
                        )}
                    {player ? (
                        <img
                            src={`/assets/flags/${player.nationality || 'WORLD'}@2x.png`}
                            onError={imgFallback}
                            alt={'flag icon'}
                            style={{
                                height: '22px',
                            }}
                        />
                    ) : null}
                </Box>
            </Stack>
            {emote ? (
                <Box sx={{ position: 'relative', display: 'flex' }}>
                    {avatar}
                    {emote}
                </Box>
            ) : (
                avatar
            )}
            {/* {gameState === GameViewState.GAME_RUNNING && player && latency && (
                <LagIndicator latency={latency} />
            )} */}
        </Stack>
    ) : null

    return (
        <Box display="flex" flexDirection="row" alignItems="center" justifyContent={'flex-end'} gap="8px" width="100%">
            {progress}
            {playerInfo}
        </Box>
    )
}
